import React from 'react'
import type { ComponentProps, PartData } from '@enonic/react-components'
import CardGridPrototype from '/react4xp/components/prototype/CardGrid'
import type { CardGridItem, CardGridLink, CardGridProps } from '/react4xp/components/prototype/CardGrid/types'
import { EditorPlaceholder } from '/react4xp/components/shared/EditorPlaceholder'

type ProcessorCard = {
	header?: string
	text?: string
	// `resolveLink` returns either undefined or a complete CardGridLink.
	link?: CardGridLink
}

type CardGridPartData = {
	overline?: string
	title?: string
	cards?: ProcessorCard[]
	// Emitted by the processor only when `cards` is empty.
	rowCount?: number
}

// The schema names the card heading `header` (PRD wording); the prototype calls it `title`.
function toCardGridItem(card: ProcessorCard): CardGridItem {
	return {
		title: card.header ?? '',
		text: card.text ?? '',
		link: card.link,
	}
}

export const CardGrid = ({ meta, data }: ComponentProps<PartData>) => {
	const { overline, title, cards = [], rowCount } = (data || {}) as CardGridPartData

	// Guard on the cards alone, not on the whole config being empty. The prototype always
	// renders its grid wrapper (`<div className="mt-8 grid …">`) — it has no zero-cards
	// branch — so a cards-less part would ship an empty grid div and its `mt-8` as dead
	// space under the heading. A Card Grid with no cards has nothing to show, so collapse
	// the whole part instead; same guard shape as ExpandableLinkList.tsx. Edit mode still
	// renders a placeholder, or the editor could not select the part to add the first card.
	if (cards.length === 0) {
		return (
			<EditorPlaceholder
				mode={meta?.mode}
				componentClass="card-grid"
				title="Card grid"
				heading={[overline, title].filter(Boolean).join(' - ')}
				reason={rowCount ? `${rowCount} card row(s) added, but all are empty.` : 'No cards added yet.'}
			/>
		)
	}

	const props: CardGridProps = {
		overline,
		title: title ?? '',
		cards: cards.map(toCardGridItem),
	}

	return <CardGridPrototype {...props} />
}
